import { css } from '@linaria/atomic';
import { onlyText } from 'react-children-utilities';
import { ColumnInstance, Row } from 'react-table';
import { PopoverMenuItem, usePopoverMenu, PopoverMenu, IconButton } from '../../../';
import { MoreIcon } from '../../../../icon';
import { TableData } from '../../table-data-type';
import { RowAction } from '../../table-types';
import { TableTrackingId } from '../../utils';

const actionsCellIconBtnCenteredStyle = css`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

type ActionCellProps<T extends TableData> = {
  rowData: Row<T>['original'];
  rowObject: Row<T>;
  column: ColumnInstance<T>;
  tableTrackingIds: TableTrackingId;
};

export const ActionsCell = <T extends TableData>({
  rowData,
  rowObject,
  column,
  tableTrackingIds,
}: ActionCellProps<T>) => {
  const { getTriggerProps, getMenuProps, getItemProps, close } = usePopoverMenu({
    interactionOptions: {
      listNavigation: {
        openOnArrowKeyDown: false,
      },
    },
    placement: 'left',
  });

  const triggerProps = getTriggerProps();
  const menuProps = getMenuProps();
  const actions = column?.actions;
  const menuLabel = column?.rowActionMenuLabel;

  const hiddenActions = () => {
    if (actions) {
      return actions?.reduce((acc, { hide }) => {
        if (typeof hide !== 'function' ? hide : hide(rowData)) {
          return acc + 1;
        }
        return acc;
      }, 0);
    }
    return 0;
  };

  const hasAllActionsHidden = hiddenActions() === actions?.length;

  const shouldRenderSingleAction = () => {
    if (!actions) {
      return false;
    }

    return (actions.length === 1 && !hasAllActionsHidden) || actions.length - hiddenActions() === 1;
  };

  type TODO = any;

  const renderActions = (
    getItemPropsFunction: ReturnType<typeof usePopoverMenu>['getItemProps'],
    showSingleButton?: boolean
  ) => {
    return actions?.map(({ onClick, Icon, renderIcon, renderCustom, ...rest }, id) => {
      const { hide, label, disabled, ...dynamicProps } = Object.entries(rest).reduce(
        (acc, [key, value]) => ({
          ...acc,
          ...(key !== 'onClick' ? { [key]: typeof value !== 'function' ? value : (value as TODO)(rowData) } : {}),
        }),
        {} as Omit<RowAction<T>, 'onClick' | 'Icon'>
      );

      const itemProps = !renderCustom
        ? getItemPropsFunction?.({
            onClick: () => {
              onClick?.(rowData, rowObject);
              close();
            },
            index: id,
          })
        : null;

      if (renderCustom) {
        return !hide ? renderCustom(getItemPropsFunction, id, close, showSingleButton, rowData, rowObject) : null;
      }

      const IconComponent = renderIcon ? (renderIcon(rowData, rowObject) as TODO) : Icon;

      return !showSingleButton
        ? !hide && (
            <PopoverMenuItem
              trackingId={tableTrackingIds.dynamicRowActionItem?.(onlyText(label as React.ReactNode))}
              key={onlyText(label as React.ReactNode) + id}
              Icon={IconComponent}
              {...itemProps}
              {...dynamicProps}
              disabled={disabled}
              label={onlyText(label as React.ReactNode)}
            >
              {label ?? ''}
            </PopoverMenuItem>
          )
        : !hide && (
            <IconButton
              key={id}
              className={actionsCellIconBtnCenteredStyle}
              onClick={() => {
                onClick?.(rowData, rowObject);
              }}
              {...dynamicProps}
              disabled={!!rowObject?.isDisabled || disabled}
              label={onlyText(label as React.ReactNode)}
              trackingId={tableTrackingIds.dynamicRowActionItem?.(onlyText(label as React.ReactNode))}
              showLabelOnHover
            >
              {IconComponent && <IconComponent color='light' />}
            </IconButton>
          );
    });
  };

  return !shouldRenderSingleAction() ? (
    <>
      <IconButton
        color='light'
        className={actionsCellIconBtnCenteredStyle}
        size='small'
        label={menuLabel ?? 'Action menu'}
        disabled={hasAllActionsHidden || !!rowObject?.isDisabled}
        trackingId={tableTrackingIds.rowActionMenu}
        {...triggerProps}
      >
        <MoreIcon color='light' size={16} />
      </IconButton>
      <PopoverMenu {...menuProps} title={menuLabel ?? ''} css={{ maxWidth: 320 }}>
        {renderActions(getItemProps)}
      </PopoverMenu>
    </>
  ) : (
    <>{renderActions(getItemProps, true)}</>
  );
};
